import React, { useEffect } from "react";
import './RecapSlide.css';
import { asset } from "../../utils/misc";

interface Props {
    animations: string[][];
    focus: boolean;
    musicUrl?: string;
    playSoundOnce?: boolean;
    soundEnabled: boolean;
    soundUrl?: string;
    children: React.ReactNode;
}

const RecapSlide: React.FC<Props> = ({ animations, focus, musicUrl, playSoundOnce, soundEnabled, soundUrl, children }) => {
    const audioRef = React.useRef<HTMLAudioElement>(null);
    const soundRef = React.useRef<HTMLAudioElement>(null);
    const [current, setCurrent] = React.useState(0);
    const [started, setStarted] = React.useState(false);
    const [played, setPlayed] = React.useState(false);
    const myRef = React.useRef<HTMLDivElement>(null);
    const defaultDuration = 250;
    const defaultDelay = 250;

    useEffect(() => {
        if (!animations || !focus || !children) return;
        if (started) return;

        setStarted(true);
        setTimeout(anim, 1000);
    }, [animations, focus, children]);

    useEffect(() => {
        if (!started) return;
        if (current < animations.length) {
            setTimeout(anim, defaultDelay);
        }
    }, [current]);

    useEffect(() => {
        if (audioRef.current) {
            if (focus && soundEnabled) {
                audioRef.current.currentTime = 0;
                audioRef.current.play();
            }
        }
    }, [focus, soundEnabled]);

    useEffect(() => {
        if (soundRef.current) {
            if (focus && (!played || !playSoundOnce)) {
                soundRef.current.currentTime = 0;
                soundRef.current.play();
                setPlayed(true);
            }
        }
    }, [focus, playSoundOnce, played]);

    function anim() {
        const anims = animations[current];
        
        Promise.all(anims.map(id => {
            const line = myRef.current && myRef.current.querySelector(`.${id}`) || null;

            if (!line) return Promise.resolve();
                                
            return new Promise<void>(resolve => { 
                const animSelf = Array.from(line.classList).some(c => c.startsWith('anim-'));
                if (animSelf) {
                    animateElement(line);
                } else if (!line.classList.contains('wait')) {
                    animateElement(line.querySelector('.anim:not(.in-view)'));
                }

                const interval = setInterval(() => {
                    const child = line.querySelector('.anim:not(.in-view)');
                    if (!child) {
                        clearInterval(interval);
                        resolve();
                        return;
                    }

                    animateElement(child);
                }, defaultDelay);
            });
        })).then(() => {
            setCurrent(current + 1);
        });
    }

    function animateElement(element) {
        if (!element) return;

        if (element.classList.contains('anim-counter-inc')) {
            numberInc(element);
            return;
        }
        
        if (element.classList.contains('anim-letters-from-left')) {
            lettersFromLeft(element);
            return;
        }

        element.classList.add('in-view');
    }

    function numberInc(element) {
        const content = element.textContent;
        let contentDone = '';
        let index = -1;
        element.innerHTML = '&nbsp;';
        element.classList.add('in-view');
        animateNextDigit();

        function animateNextDigit() {
            index++;

            if (index >= content.length) {
                return;
            }

            if (contentDone) element.textContent = contentDone;
            const target = content.charAt(index);

            if (isNaN(target)) {
                contentDone += target;
                return animateNextDigit();
            }

            let digit = target - 9;
            animateDigit();

            function animateDigit() {
                setTimeout(() => {
                    element.textContent = contentDone + Math.abs(digit).toString();
                    if (digit < parseInt(target, 10)) {
                        digit++;
                        animateDigit();
                    } else {
                        contentDone += target;
                        animateNextDigit();
                    }
                }, 25);
            }
        }
    }

    function lettersFromLeft(element) {
        const content = element.textContent;
        let contentDone = '';
        let index = -1;
        element.innerHTML = '&nbsp;';
        element.classList.add('in-view');
        animateNextDigit();

        function animateNextDigit() {
            index++;

            if (index > content.length) {
                return;
            }

            if (contentDone) element.textContent = contentDone;
            const target = content.charAt(index);

            setTimeout(() => {
                contentDone += target;
                return animateNextDigit();
            }, 50);
        }
    }

    return <div ref={myRef} className="yr-h-100">
        {children}
        <img src={asset('/dist/images/year-recap/logo.png')} style={{ position: 'absolute', bottom: '2rem', right: '2rem', width: '100px', opacity: 0.5 }} />
        {musicUrl && <audio ref={audioRef} src={musicUrl} autoPlay={false} loop muted={!(focus && soundEnabled)} />}
        {soundUrl && <audio ref={soundRef} src={soundUrl} autoPlay={false} loop={!playSoundOnce} muted={!(focus && soundEnabled)} />}
    </div>;
}

export default RecapSlide;